<template>
  <div>
    <div class="panel m-3">
      <PanelHeader :panel-header="medicationName || 'Medikament'" />
      <div class="container pt-3 pb-3">
        <div
          v-if="dataObject"
          class="stammdaten"
        >
          <div class="stammdaten-facts">
            <p class="title is-5 mb-2">
              {{ factsLine }}
            </p>
            <div class="kv-columns">
              <div class="kv-grid">
                <template v-if="medicationDrug">
                  <span>Wirkstoff:</span>
                  <span>{{ medicationDrug }}</span>
                </template>
                <template v-if="className">
                  <span>Klasse:</span>
                  <span>{{ className }}</span>
                </template>
                <span>Anzahl/(Füll-)Menge:</span>
                <span>{{ medicationMax }}</span>
                <template v-if="unitLine">
                  <span>Darreichungsform (Einheit):</span>
                  <span>{{ unitLine }}</span>
                </template>
                <template v-if="formName">
                  <span>Darreichungsform (Art):</span>
                  <span>{{ formName }}</span>
                </template>
                <template v-if="medicationDisplayName">
                  <span>Anzeigename:</span>
                  <span>{{ medicationDisplayName }}</span>
                </template>
              </div>
            </div>
            <template v-if="medicationHint">
              <hr class="my-2">
              <p>{{ medicationHint }}</p>
            </template>
          </div>
        </div>
      </div>
    </div>

    <section class="mx-5 my-2">
      <div
        class="buttons"
        style="display: flex"
      >
        <b-button
          class="is-primary"
          tag="router-link"
          to="/medication"
        >
          Zurück
        </b-button>
        <b-button
          class="is-danger"
          style="margin-left: auto"
          @click="isDeleteCheck = true"
        >
          Löschen
        </b-button>
        <b-button
          class="is-primary"
          @click="triggerEdit"
        >
          Bearbeiten
        </b-button>
      </div>
    </section>
    <ModalComponent
      v-if="isDeleteCheck && dataObject"
      :item="medicationName"
      :is-delete-check="isDeleteCheck"
      :is-loading="isLoading"
      :cancel="() => isDeleteCheck = false"
      :submit="submitDelete"
    />
  </div>
</template>

<script>
/**
 * This file is for viewing a focused medication.
 */
import Parse from "parse/dist/parse.min.js";
import PanelHeader from "@/components/helperComponents/PanelHeader";
import ModalComponent from "@/components/helperComponents/ModalComponent";
import { negativeFeedBack, positiveFeedBack } from "@/lib/Toast";

export default {
	name: "ViewMedication",
	components: {
		ModalComponent,
		PanelHeader,
	},
	data() {
		return {
			dataObject: undefined,
			isDeleteCheck: false,
			isLoading: false,
		};
	},
	computed: {
		medicationName() {
			const name = this.dataObject && this.dataObject.attributes.name;
			return name ? name.trim() : "";
		},
		factsLine() {
			if (!this.dataObject) return "";
			const potency = this.dataObject.attributes.potency;
			return [ this.medicationName, potency ? potency.trim() : "" ]
				.filter(Boolean).join(", ");
		},
		medicationDrug() {
			const drug = this.dataObject && this.dataObject.attributes.drug;
			return drug ? drug.trim() : "";
		},
		medicationMax() {
			return this.dataObject ? this.dataObject.attributes.max : "";
		},
		medicationDisplayName() {
			const name =
				this.dataObject && this.dataObject.attributes.displayName;
			return name ? name.trim() : "";
		},
		medicationHint() {
			const hint = this.dataObject && this.dataObject.attributes.hint;
			return hint ? hint.trim() : "";
		},
		className() {
			const cls =
				this.dataObject && this.dataObject.attributes.medicationClass;
			return cls && cls.attributes && cls.attributes.name
				? cls.attributes.name : "";
		},
		unitLine() {
			const unit =
				this.dataObject && this.dataObject.attributes.medicationUnit;
			if (!unit || !unit.attributes || !unit.attributes.name) return "";
			return `${unit.attributes.name} (${unit.attributes.symbol})`;
		},
		formName() {
			const form =
				this.dataObject && this.dataObject.attributes.medicationForm;
			return form && form.attributes && form.attributes.name
				? form.attributes.name : "";
		}
	},
	async mounted() {
		this.scrollToElement();
		let dataObject = this.$route.params.data;
		// Reached via browser navigation (back/forward, reload, deep
		// link): the in-memory object is gone, load it by the URL id.
		if (!dataObject && this.$route.params.id) {
			try {
				dataObject = await new Parse.Query("MedicationMedication")
					.include(
						[ "medicationForm", "medicationUnit",
							"medicationClass" ])
					.get(this.$route.params.id);
			}
			catch (e) {
				console.error(e);
			}
		}
		if (!dataObject) return await this.$router.push("/medication");
		this.dataObject = dataObject;
	},
	methods: {
		scrollToElement() {
			const el = this.$parent.$parent.$refs.scrollToMe;
			if (el) {
				el.scrollIntoView({ behavior: "smooth" });
			}
		},
		triggerEdit() {
			this.$router.push(
				{ name: "AddMedication", params: { data: this.dataObject } });
		},
		async submitDelete() {
			this.isLoading = true;
			try {
				this.dataObject.set("deleted", true);
				await this.dataObject.save();
				this.$store.dispatch("medication/delete", this.dataObject);
				this.isDeleteCheck = false;
				await this.$router.push("/medication");
				positiveFeedBack(this.medicationName + " gelöscht!");
			}
			catch (e) {
				console.error(e);
				negativeFeedBack(this.medicationName + " nicht gelöscht!");
			}
			finally {
				this.isLoading = false;
			}
		}
	},
}
</script>

<style scoped>
.stammdaten {
	display: flex;
	align-items: flex-start;
	gap: 1.5rem;
	padding-left: 1.5rem;
}

.stammdaten-facts {
	text-align: left;
	min-width: 0;
}

.stammdaten-facts p {
	margin-bottom: 0.25rem;
}

.kv-columns {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 0.25rem 3.5rem;
}

.kv-grid {
	display: grid;
	/* Value column may shrink so long values wrap, not overflow. */
	grid-template-columns: max-content minmax(0, max-content);
	column-gap: 0.5rem;
	row-gap: 0.25rem;
	min-width: 0;
}
</style>
